import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useTranslation } from 'react-i18next';
import { Key } from 'phosphor-react';
import SInputField from '@/components/ui/molecules/SInputField';
import SButton from '@/components/ui/atoms/SButton';
import { ThemeToggle } from '@/components/ui/atoms/ThemeToggle';
import { useTheme } from '@/context/ThemeContext';
import mainLogo from '../../../assets/main_logo.png';
import styles from './Login.module.css';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/context/ToastContext';

type LoginTab = 'email' | 'phone';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  /* Where the guard interrupted them — a Community connect link, say. Without
     this every login lands on Home and the link they followed is lost. Mirrors
     the v2 login; both are reachable depending on the UI flag. */
  const returnTo: string | undefined =
    routerLocation.state?.from?.pathname &&
    `${routerLocation.state.from.pathname}${routerLocation.state.from.search ?? ''}`;
  const { t } = useTranslation();
  const { requestOtp, verifyOtpAndLogin } = useAuth();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { showToast } = useToast();
  const { theme } = useTheme();
  const welcomeName = localStorage.getItem('welcomeName') ?? '';
  const [showSplash, setShowSplash] = useState(false);
  const [activeTab] = useState<LoginTab>('email');
  const [email, setEmail] = useState('');
  const [phone] = useState('');
  const [countryCode] = useState('+1');
  const [step, setStep] = useState<'enter' | 'verify'>('enter');
  const [otp, setOtp] = useState('');
  const [otpTarget, setOtpTarget] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [agreedToTerms] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowSplash(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  const startResendTimer = () => setResendCooldown(30);

  const formatCountdown = (totalSeconds: number) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const interval = setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [resendCooldown]);

  const handleRequestOtpEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    try {
      await requestOtp(email, 'email');
      setOtpTarget(email);
      setStep('verify');
      setOtp('');
      startResendTimer();
    } catch (err: any) {
      showToast({
        variant: 'error',
        title: t('login.toast.invalidCredentials.title'),
        message: err.message || t('login.toast.invalidCredentials.message'),
        ctaText: t('common.contactTeeBox'),
        onCtaClick: () => {},
      });
    }
  };

  const handleRequestOtpPhone = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone) return;
    try {
      const target = `${countryCode}${phone}`;
      await requestOtp(target, 'sms');
      setOtpTarget(target);
      setStep('verify');
      setOtp('');
      startResendTimer();
    } catch (err: any) {
      showToast({
        variant: 'error',
        title: t('login.toast.invalidCredentials.title'),
        message: err.message || t('login.toast.invalidCredentials.message'),
        ctaText: t('common.contactTeeBox'),
        onCtaClick: () => {},
      });
    }
  };

  const handleVerifyLogin = async (otpValue: string) => {
    if (!executeRecaptcha) {
      showToast({
        variant: 'error',
        title: t('login.toast.recaptchaNotReady.title'),
        message: t('login.toast.recaptchaNotReady.message'),
        ctaText: t('common.contactTeeBox'),
        onCtaClick: () => {},
      });
      return;
    }
    setIsVerifying(true);
    try {
      const recaptchaToken = await executeRecaptcha('login');
      const send_to = activeTab === 'email' ? email : `${countryCode}${phone}`;
      const mode = activeTab === 'email' ? 'email' : 'sms';
      await verifyOtpAndLogin(otpValue, send_to, mode, recaptchaToken);
      navigate(returnTo ?? '/home', { replace: true });
    } catch (err: any) {
      showToast({
        variant: 'error',
        title: t('login.toast.invalidCredentials.title'),
        message: err.message || t('login.toast.invalidCredentials.message'),
        ctaText: t('common.contactTeeBox'),
        onCtaClick: () => {},
      });
      setIsVerifying(false);
    }
  };

  useEffect(() => {
    if (otp.length === 6 && !isVerifying) {
      handleVerifyLogin(otp);
    }
  }, [otp]);

  const handleChangeAddress = () => {
    setStep('enter');
    setOtp('');
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    try {
      const send_to = activeTab === 'email' ? email : `${countryCode}${phone}`;
      const mode = activeTab === 'email' ? 'email' : 'sms';
      await requestOtp(send_to, mode);
      setOtp('');
      startResendTimer();
      showToast({
        variant: 'success',
        title: t('login.toast.otpResent.title'),
        message: t('login.toast.otpResent.message'),
        ctaText: t('common.contactTeeBox'),
        onCtaClick: () => {},
      });
    } catch (err: any) {
      showToast({
        variant: 'error',
        title: t('login.toast.invalidCredentials.title'),
        message: err.message || t('login.toast.invalidCredentials.message'),
        ctaText: t('common.contactTeeBox'),
        onCtaClick: () => {},
      });
    }
  };

  if (showSplash) {
    return (
      <div className={styles.splashScreen}>
        <img src={mainLogo} alt={t('login.logoAlt')} className={styles.splashLogo} />
      </div>
    );
  }

  const inputTheme = theme === 'dark' ? 'dark' : 'light';

  return (
    <div className={styles.loginPage}>
      {/* ── Hero ──────────────────────────────────────────── */}
      <div className={styles.hero}>
        <div className={styles.topBar}>
          <ThemeToggle />
        </div>
        <div className={styles.logoWrap}>
          <img src={mainLogo} alt={t('login.logoAlt')} className={styles.logo} />
          <span className={styles.heroLabel}>{welcomeName ? 'Welcome back' : 'Welcome'}</span>
          {step === 'enter' ? (
            <>
              <h1 className={styles.heroTitle}>
                {welcomeName
                  ? t('login.welcomeBack', { name: welcomeName })
                  : t('login.welcomeFirst')}
              </h1>
              <p className={styles.heroSubtitle}>
                {welcomeName ? t('login.enterDetailsReturning') : t('login.enterDetails')}
              </p>
            </>
          ) : (
            <>
              <h1 className={styles.heroTitle}>{t('login.verifyOTP')}</h1>
              <p className={styles.heroSubtitle}>{t('login.enterOTPCode')}</p>
            </>
          )}
        </div>
      </div>

      {/* ── Form card ─────────────────────────────────────── */}
      <div className={styles.formCard}>
        {/* Tabs - hidden until mobile option is restored
        {step === 'enter' && (
          <div className={styles.tabsSection}>
            <SButton
              size="sm"
              variant={activeTab === 'email' ? 'primary' : 'outline'}
              label={t('login.email')}
              onClick={() => setActiveTab('email')}
              className={styles.tabButton}
            />
            {/* MOBILE OPTION - commented out temporarily
            <SButton
              size="sm"
              variant={activeTab === 'phone' ? 'primary' : 'outline'}
              label={t('login.phone')}
              onClick={() => setActiveTab('phone')}
              className={styles.tabButton}
            />
            * /}
          </div>
        )}
        */}

        {/* Form */}
        {step === 'enter' ? (
          <form
            className={styles.formSection}
            onSubmit={activeTab === 'email' ? handleRequestOtpEmail : handleRequestOtpPhone}
          >
            {activeTab === 'email' ? (
              <div className={styles.inputWrapper}>
                <SInputField
                  variant="email"
                  size="s"
                  theme={inputTheme}
                  placeholder={t('login.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  showIcon={true}
                  labelText={t('login.emailAddress')}
                />
              </div>
            ) : null}
            {/* MOBILE OPTION - commented out temporarily
            {activeTab === 'phone' && (
              <div className={styles.phoneInputContainer}>
                <span className={styles.inputLabel}>{t('login.phoneNumber')}</span>
                <div className="s-input-row">
                  <SInputField
                    variant="country"
                    size="s"
                    theme={inputTheme}
                    countryFlag={countryFlag}
                    countryDialCode={countryCode}
                    onCountryClick={handleCountryClick}
                    onCountryChange={handleCountryChange}
                  />
                  <SInputField
                    variant="phone"
                    size="s"
                    theme={inputTheme}
                    inputMode="numeric"
                    placeholder={t('login.phonePlaceholder')}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                    showIcon
                  />
                </div>
              </div>
            )}
            */}

            {/* MOBILE OPTION - commented out temporarily
            {activeTab === 'phone' && (
              <div className={styles.consentSection}>
                <label className={styles.consentRow}>
                  <input
                    type="checkbox"
                    className={styles.consentCheckbox}
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                  />
                  <span className={styles.consentText}>{t('login.optInText')}</span>
                </label>
                <div className={styles.consentLinks}>
                  <a href="https://tbx.golf/terms-of-use" target="_blank" rel="noopener noreferrer" className={styles.consentLink}>
                    {t('login.termsOfUse')}
                  </a>
                  <span className={styles.consentDot}>·</span>
                  <a href="https://tbx.golf/privacy-policy" target="_blank" rel="noopener noreferrer" className={styles.consentLink}>
                    {t('login.privacyPolicy')}
                  </a>
                </div>
              </div>
            )}
            */}

            {(() => {
              const isDisabled = activeTab === 'phone'
                ? !phone || !agreedToTerms
                : !email;
              return (
                <SButton
                  type="submit"
                  size="lg"
                  variant={isDisabled ? 'disabled' : 'primary'}
                  disabled={isDisabled}
                  label={t('login.requestOTP')}
                />
              );
            })()}
          </form>
        ) : (
          <div className={styles.formSection}>
            <button type="button" className={styles.backLink} onClick={handleChangeAddress}>
              ← {t('login.back')}
            </button>

            <div className={styles.otpInfoCard}>
              <span className="body-sm-muted">{t('login.otpSentTo')}</span>
              <span className="body-sm-action-semibold">{otpTarget}</span>
            </div>

            <div className={styles.inputWrapper}>
              <SInputField
                variant="phone"
                size="s"
                theme={inputTheme}
                state="active"
                labelText={t('login.enterOTP')}
                placeholder={t('login.otpPlaceholder')}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                showIcon
                icon={<Key size={20} weight="regular" />}
                maxLength={6}
              />
            </div>

            {isVerifying ? (
              <div className={styles.verifyingRow}>
                <span className={styles.verifyingSpinner} />
                <span className={styles.verifyingText}>{t('login.verifying')}</span>
              </div>
            ) : (
              <div className={styles.otpActionsRow}>
                {resendCooldown > 0 ? (
                  <span className={styles.resendCountdownText}>
                    {t('login.resendAvailableIn', { time: formatCountdown(resendCooldown) })}
                  </span>
                ) : (
                  <span className={styles.resendCountdownText}>
                    {t('login.resendPrompt')}{' '}
                    <button type="button" className={styles.resendLink} onClick={handleResendOtp}>
                      {t('login.resendOTP')}
                    </button>
                  </span>
                )}
              </div>
            )}

          </div>
        )}

      </div>
    </div>
  );
};

export default Login;
